if (!customElements.get("swiper-slide")) {
  class SwiperSlide extends HTMLElement {
    constructor() {
      super();

      this.mainSwiper = null;
      this.thumbsSwiper = null;

      this.mainSwiperEl = null;
      this.thumbsSwiperEl = null;

      this.resizeObserver = null;
      this.heightRaf = null;
      this.imageLoadHandlers = [];

      this.handleMatchedHeightUpdate = this.updateMatchedThumbHeight.bind(this);
    }

    connectedCallback() {
      if (this.dataset.swiperReady === "true") return;

      const swiperSelector = this.getAttribute("main-swiper");
      const swiperEl = swiperSelector
        ? this.querySelector(swiperSelector)
        : null;

      if (!swiperEl || typeof Swiper === "undefined") return;

      this.dataset.swiperReady = "true";

      this.mainSwiperEl = swiperEl;

      const thumbsSelector = this.getAttribute("thumbs");
      const thumbsEl = thumbsSelector
        ? this.querySelector(thumbsSelector)
        : null;

      this.thumbsSwiperEl = thumbsEl;

      const shouldMatchThumbsHeight = this.hasAttribute("match-thumbs-height");

      if (shouldMatchThumbsHeight && thumbsEl) {
        this.setupMatchedThumbHeight();
      }

      if (thumbsEl) {
        const thumbsOptions = {
          direction: this.getAttribute("thumbs-direction") || "horizontal",
          slidesPerView: this.numberAttr("thumbs-slides-per-view", 4),
          spaceBetween: this.numberAttr("thumbs-space-between", 10),
          watchSlidesProgress: this.hasAttribute("thumbs-watch-progress"),
          slideToClickedSlide: this.hasAttribute("thumbs-click-to-slide"),
        };

        const thumbsBreakpoints = this.getAttribute("thumbs-breakpoints");

        if (thumbsBreakpoints) {
          try {
            thumbsOptions.breakpoints = JSON.parse(thumbsBreakpoints);
          } catch (e) {
            console.warn("Invalid thumbs-breakpoints JSON", e);
          }
        }

        this.thumbsSwiper = new Swiper(thumbsEl, thumbsOptions);
      }

      const options = {
        slidesPerView: this.numberAttr("slides-per-view", 1),
        spaceBetween: this.numberAttr("space-between", 0),
        centeredSlides: this.hasAttribute("centered"),
        loop: this.hasAttribute("loop"),
        initialSlide: this.numberAttr("initial-slide", 0),
        slideToClickedSlide: this.hasAttribute("clicked-to-slide"),
        speed: this.numberAttr("speed", 300),
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
      };

      const effect = this.getAttribute("effect") || "slide";

      if (effect && effect !== "slide") {
        options.effect = effect;

        if (effect === "fade") {
          options.slidesPerView = 1;
          options.spaceBetween = 0;
          options.fadeEffect = {
            crossFade: this.hasAttribute("fade-cross-fade"),
          };
        }

        if (effect === "cube") {
          options.slidesPerView = 1;
          options.spaceBetween = 0;
          options.cubeEffect = {
            shadow: this.hasAttribute("cube-shadow"),
            slideShadows: this.hasAttribute("cube-slide-shadows"),
            shadowOffset: this.numberAttr("cube-shadow-offset", 20),
            shadowScale: this.numberAttr("cube-shadow-scale", 0.94),
          };
        }

        if (effect === "coverflow") {
          options.coverflowEffect = {
            rotate: this.numberAttr("coverflow-rotate", 50),
            stretch: this.numberAttr("coverflow-stretch", 0),
            depth: this.numberAttr("coverflow-depth", 100),
            modifier: this.numberAttr("coverflow-modifier", 1),
            slideShadows: this.hasAttribute("coverflow-slide-shadows"),
          };
        }

        if (effect === "flip") {
          options.slidesPerView = 1;
          options.spaceBetween = 0;
          options.flipEffect = {
            slideShadows: this.hasAttribute("flip-slide-shadows"),
            limitRotation: true,
          };
        }

        if (effect === "creative") {
          options.slidesPerView = 1;
          options.spaceBetween = 0;
          options.creativeEffect = {
            prev: {
              shadow: false,
              translate: ["-20%", 0, -1],
            },
            next: {
              translate: ["100%", 0, 0],
            },
          };
        }

        if (effect === "cards") {
          options.slidesPerView = 1;
          options.spaceBetween = 0;
          options.cardsEffect = {
            slideShadows: this.hasAttribute("cards-slide-shadows"),
          };
        }
      }

      const nextSel = this.getAttribute("next");
      const prevSel = this.getAttribute("prev");

      if (nextSel && prevSel) {
        options.navigation = {
          nextEl: this.querySelector(nextSel),
          prevEl: this.querySelector(prevSel),
        };
      }

      const paginationSel = this.getAttribute("pagination");
      const paginationType = this.getAttribute("pagination-type");

      if (paginationSel) {
        options.pagination = {
          el: this.querySelector(paginationSel),
          clickable: true,
        };

        if (paginationType) {
          options.pagination.type = paginationType;
        }
      }

      const breakpoints = this.getAttribute("breakpoints");

      if (breakpoints) {
        try {
          options.breakpoints = JSON.parse(breakpoints);
        } catch (e) {
          console.warn("Invalid breakpoints JSON", e);
        }
      }

      if (this.thumbsSwiper) {
        options.thumbs = {
          swiper: this.thumbsSwiper,
        };
      }

      this.mainSwiper = new Swiper(swiperEl, options);

      if (shouldMatchThumbsHeight && thumbsEl) {
        this.bindMatchedHeightEvents();
        this.updateMatchedThumbHeight();
      }
    }

    setupMatchedThumbHeight() {
      if (!this.mainSwiperEl || !this.thumbsSwiperEl) return;

      this.resizeObserver = new ResizeObserver(() => {
        this.updateMatchedThumbHeight();
      });

      this.resizeObserver.observe(this.mainSwiperEl);

      const images = this.querySelectorAll("img");

      images.forEach((image) => {
        const handler = () => {
          this.updateMatchedThumbHeight();
        };

        image.addEventListener("load", handler);
        this.imageLoadHandlers.push({ image, handler });

        if (image.complete) {
          this.updateMatchedThumbHeight();
        }
      });

      window.addEventListener("resize", this.handleMatchedHeightUpdate);
      window.addEventListener("load", this.handleMatchedHeightUpdate);
    }

    bindMatchedHeightEvents() {
      if (!this.mainSwiper) return;

      this.mainSwiper.on("slideChange", () => {
        this.updateMatchedThumbHeight();
      });

      this.mainSwiper.on("transitionEnd", () => {
        this.updateMatchedThumbHeight();
      });

      this.mainSwiper.on("resize", () => {
        this.updateMatchedThumbHeight();
      });

      if (this.thumbsSwiper) {
        this.thumbsSwiper.on("resize", () => {
          this.updateMatchedThumbHeight();
        });
      }
    }

    updateMatchedThumbHeight() {
      if (!this.mainSwiperEl || !this.thumbsSwiperEl) return;

      if (this.heightRaf) {
        cancelAnimationFrame(this.heightRaf);
      }

      this.heightRaf = requestAnimationFrame(() => {
        let height = this.mainSwiperEl.offsetHeight;

        if ((!height || height < 2) && this.mainSwiperEl.offsetWidth) {
          const activeImage =
            this.mainSwiperEl.querySelector(".swiper-slide-active img") ||
            this.mainSwiperEl.querySelector("img");

          if (activeImage && activeImage.naturalWidth) {
            height =
              this.mainSwiperEl.offsetWidth *
              (activeImage.naturalHeight / activeImage.naturalWidth);
          }
        }

        if (!height || height < 2 || height > 10000) return;

        this.style.setProperty(
          "--swiper-main-height",
          `${Math.round(height)}px`,
        );

        if (this.thumbsSwiper) {
          this.thumbsSwiper.update();
        }
      });
    }

    disconnectedCallback() {
      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }

      if (this.thumbsSwiper) {
        this.thumbsSwiper.destroy(true, true);
        this.thumbsSwiper = null;
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.heightRaf) {
        cancelAnimationFrame(this.heightRaf);
        this.heightRaf = null;
      }

      this.imageLoadHandlers.forEach(({ image, handler }) => {
        image.removeEventListener("load", handler);
      });

      this.imageLoadHandlers = [];

      window.removeEventListener("resize", this.handleMatchedHeightUpdate);
      window.removeEventListener("load", this.handleMatchedHeightUpdate);

      this.dataset.swiperReady = "false";
    }

    numberAttr(name, fallback) {
      const val = this.getAttribute(name);
      return val !== null ? Number(val) : fallback;
    }
  }

  customElements.define("swiper-slide", SwiperSlide);
}
